import React, { useEffect, useMemo, useState } from 'react';

import { useObservability } from 'shared/helpers/observability';
import { Text, Box } from '@opendoor/bricks/core';

import { Wizard, WizardProps } from 'shared/components/Wizard';
import { ClosingTaskWizardLayout } from 'sellDirect/components/closingDash/ClosingTaskWizardLayout';
import { HelloSignWizardLayout } from 'sellDirect/components/closingDash/HelloSignWizardLayout';
import { ContractSummary } from 'sellDirect/components/closingDash/contract/ContractSummary';
import { SetUpPurchaseContractForm } from 'sellDirect/components/closingDash/contract/SetUpPurchaseContractForm';

import { ClosingTasks } from 'sellDirect/helpers/closingTasks';
import { getHubSellingRoute } from 'shared/helpers/routes';
import {
  HelloSignStep,
  ContractData,
} from 'sellDirect/components/closingDash/helloSign/HelloSignStep';
import { useHistory } from 'platform/HistoryProvider';
import { SignContractResult } from 'sellDirect/graphql/useSignContract';
import { showToast } from '@opendoor/bricks/complex/Toast';
import { FinalizedOffer } from 'sellDirect/OfferContext';
import { HelloSignWizardStep } from 'shared/helpers/constants';
import { isPurchaseAgreementInProgress } from 'sellDirect/helpers/closingDash/contracts';
import { generatePath } from 'react-router-dom';
import { useQualificationContext } from 'shared/helpers/QualificationContext';

export interface ContractPageProps {
  /** Finalized offer */
  offer: FinalizedOffer;
  /** Current closing tasks */
  closingTasks: ClosingTasks;
}

/**
 * Wizard that allows users to set up and sign their contract
 *
 * @throws Error if the offer is expired
 *
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const ContractWizard: React.FC<ContractPageProps> = ({ offer, closingTasks }) => {
  const { addressUUID } = useQualificationContext();
  const { trackError } = useObservability();
  const { history, goBack } = useHistory();
  const initialLocation = useMemo(() => history.location, [history]);
  const [contractData, setContractData] = useState<ContractData>();
  // checks to see if step 1 information has been completed
  const hasFilledOutTheContractInformation =
    closingTasks.hoaFees?.complete &&
    closingTasks.setCloseDate?.complete &&
    closingTasks.setSignatories?.complete;

  const signers = closingTasks.signCombinedPurchaseAgreement?.taskable.signers;
  const isContractInProgress = isPurchaseAgreementInProgress(signers);

  // Offer should not be expired if we're in the contract wizard
  if (offer.isExpired) {
    throw new Error('Contract page expects the offer to not be expired');
  }

  // Javi - should this still be here?
  // signCombinedPurchaseAgreement and setSignatories should be set if here.
  // Silently log an error if not to see how often this happens.
  // TODO(LE-2991): Add fallback if this happens often.
  useEffect(() => {
    if (!closingTasks.signCombinedPurchaseAgreement) {
      trackError('ContractPage missing signCombinedPurchaseAgreement');
    }
    if (!closingTasks.setSignatories) {
      trackError('ContractPage missing setSignatories');
    }
  }, [closingTasks, trackError]);

  const steps: WizardProps['steps'] = [
    {
      content: ({ goToNextStep }) => (
        <ClosingTaskWizardLayout>
          <Box mt="10" mb="9">
            <SetUpPurchaseContractForm
              offer={offer}
              closingTasks={closingTasks}
              goBackToSummary={goToNextStep}
            />
          </Box>
        </ClosingTaskWizardLayout>
      ),
    },
    {
      hideGoBack: true,
      content: ({ goToNextStep, goToPreviousStep }) => (
        <ClosingTaskWizardLayout>
          <Box mt="10" mb="9">
            <ContractSummary
              offer={offer}
              closingTasks={closingTasks}
              goToEditForm={goToPreviousStep}
              disableEdit={isContractInProgress}
              onClickSignButton={(signer, helloSignUrl) => {
                setContractData({
                  signer,
                  helloSignUrl,
                });
                goToNextStep();
              }}
            />
          </Box>
        </ClosingTaskWizardLayout>
      ),
    },
    {
      // eslint-disable-next-line react/display-name
      content: ({ goToPreviousStep }) => {
        if (!closingTasks.signCombinedPurchaseAgreement) {
          throw new Error(
            'SignPurchaseContract expects there to be a signedCombinedPurchaseAgreement task',
          );
        }
        if (!contractData) {
          throw new Error('SignPurchaseContract expects a contractData to be set');
        }
        return (
          <HelloSignWizardLayout>
            <HelloSignStep
              contractData={contractData}
              helloSignTaskName="signCombinedPurchaseAgreementAndRepairAddendum"
              offerId={offer.id}
              onTaskCompletion={async (result) => {
                if (result === SignContractResult.success_fully_signed) {
                  await offer.refetchWebLead();
                  history.push(generatePath(getHubSellingRoute(), { addressUUID }));
                } else if (result === SignContractResult.success_not_fully_signed) {
                  goToPreviousStep();
                } else {
                  goToPreviousStep();
                  showToast(
                    'error',
                    'Oops, something went wrong. Please try again in a few hours or contact your experience partner.',
                    'cake-hellosign-sign-combined-purchase-agreement-and-repair-addendum-error-toast',
                    { toastOptions: { position: 'bottom-left' } },
                  );
                }
              }}
            />
          </HelloSignWizardLayout>
        );
      },
      confirmOnClose: {
        cancelText: 'Finish signing contract',
        confirmText: 'Close without saving',
        content: (
          <Text mb="8">
            You’re almost done signing {offer.shouldSeeAgentExperience ? 'the' : 'your'} contract.
            If you close the window, you’ll have to start over when you return.
          </Text>
        ),
        title: 'Are you sure you want to stop?',
      },
    },
  ];

  return (
    <Wizard
      title={`Set up and sign ${offer.shouldSeeAgentExperience ? 'the' : 'your'} contract`}
      steps={steps}
      initialStepNumber={
        hasFilledOutTheContractInformation
          ? HelloSignWizardStep.SUMMARY_STEP
          : HelloSignWizardStep.FORM_STEP
      }
      hideFooter={!!contractData}
      onClose={() =>
        goBack({
          before: initialLocation,
          fallbackLocation: getHubSellingRoute(),
          addressUUID,
        })
      }
    />
  );
};
